import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { TreatmentType } from "@/lib/database.types";

// Open intake drafts (incomplete + not expired) for the signed-in user.
// RLS on intake_drafts permits select where email matches the JWT email, so
// we don't have to filter explicitly — the policy does it.
//
// Used to surface a "Resume your assessment" card on the dashboard for
// patients who started a different pathway's intake but didn't finish.

export interface OpenIntakeDraft {
  id: string;
  treatment_type: TreatmentType;
  last_step: string;
  step_index: number;
  resume_token: string;
  started_at: string;
  last_progress_at: string;
  expires_at: string;
}

export function useIntakeDrafts(email: string | undefined) {
  return useQuery<OpenIntakeDraft[]>({
    queryKey: ["intake_drafts", email],
    enabled: !!email,
    // Drafts move slowly — once a minute is plenty.
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("intake_drafts")
        .select(
          "id, treatment_type, last_step, step_index, resume_token, started_at, last_progress_at, expires_at",
        )
        .order("last_progress_at", { ascending: false });
      if (error) throw error;
      return (data as OpenIntakeDraft[] | null) ?? [];
    },
  });
}
